import React from 'react';
import { Box, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Pagination, CircularProgress } from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

function SprintUpdatesReport(props) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [sprints, setSprints] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  
  const [selectedSprint, setSelectedSprint] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState('');
  
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = React.useState(0);

  React.useEffect(() => {
    getRequest(GET_URL.sprints.api, { limit: 500, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        setSprints(response.data?.data || []);
      }
    });
    getRequest(GET_URL.user.api, {}, props).then((response) => {
      if (response && response.status === 200) {
        setUsers(response.data?.data || []);
      }
    });
  }, [props]);

  const loadData = React.useCallback(() => {
    setLoading(true);
    const params = {
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
    };
    if (selectedSprint) params.sprint = selectedSprint;
    if (selectedUser) params.assigned_to = selectedUser;

    getRequest(GET_URL.sprint_daily_updates.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          setRows(response.data?.data || []);
          setRowCount(response.data?.count || 0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [paginationModel.page, paginationModel.pageSize, selectedSprint, selectedUser, props]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const groupedByDate = React.useMemo(() => {
    const grouped = {};
    (rows || []).forEach((row) => {
      if (!grouped[row.update_date]) grouped[row.update_date] = [];
      grouped[row.update_date].push(row);
    });
    return grouped;
  }, [rows]);

  const sortedDates = React.useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedByDate]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>Sprint Updates</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View all updates entered in task updates, filtered by sprint or assigned user.
      </Typography>
      
      <Paper sx={{ mt: 2, p: 2, borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="sprint-filter-label">Sprint</InputLabel>
              <Select
                labelId="sprint-filter-label"
                label="Sprint"
                value={selectedSprint}
                onChange={(e) => {
                  setSelectedSprint(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">
                  <em>All Sprints</em>
                </MenuItem>
                {sprints.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="user-filter-label">Assigned To</InputLabel>
              <Select
                labelId="user-filter-label"
                label="Assigned To"
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">
                  <em>All Users</em>
                </MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>{u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2, minHeight: 400 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {sortedDates.map((date) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={date}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 1.5, bgcolor: '#fafafa' }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, borderBottom: '1px solid #e2e8f0', pb: 0.5 }}>
                      {date}
                    </Typography>
                    {groupedByDate[date].map((entry) => (
                      <Paper key={entry.id} elevation={0} sx={{ mb: 1.5, p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight={700} color="primary" display="block" sx={{ mb: 0.5 }}>
                          {entry.updated_by_name}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>
                          {entry.update_text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Sprint: {entry.sprint_name}
                        </Typography>
                      </Paper>
                    ))}
                  </Paper>
                </Grid>
              ))}
              {sortedDates.length === 0 && (
                <Typography variant="body2" sx={{ width: '100%', textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  No updates found.
                </Typography>
              )}
            </Grid>

            {rowCount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {paginationModel.page * paginationModel.pageSize + 1} to {Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount)} of {rowCount} updates
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small">
                    <Select
                      value={paginationModel.pageSize}
                      onChange={(e) => setPaginationModel((prev) => ({ ...prev, pageSize: e.target.value, page: 0 }))}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value={10}>10 per page</MenuItem>
                      <MenuItem value={20}>20 per page</MenuItem>
                      <MenuItem value={50}>50 per page</MenuItem>
                      <MenuItem value={100}>100 per page</MenuItem>
                    </Select>
                  </FormControl>
                  <Pagination
                    count={Math.ceil(rowCount / paginationModel.pageSize)}
                    page={paginationModel.page + 1}
                    onChange={(e, value) => setPaginationModel((prev) => ({ ...prev, page: value - 1 }))}
                    color="primary"
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default SprintUpdatesReport;
